import React, {memo, useCallback, useMemo, useState} from 'react';
import {Container, Flex, Text, ToggleSwitch} from '@priority-software/priority-style-react';
import cx from 'classnames';
import {useHover} from '../../../../hooks/useHover';

import Control from './Controls/Control';
import Operators from './Operators';
import styles from './styles.module.scss';
import TagsArea from './TagsArea';
import {APP_SEARCH_CURR_USER} from '../../consts';
import {SortIcon} from '../../../../components/Common/SortIcon';
import {getLocalizedText, texts} from '../../../../constants/TextConst';
import {Operator} from '../../../../_legacy/Searches/consts/operators';
import {ColumnType} from '../../types/column';
import {ISearchCondition} from '../../types/filters';
import {ISearchField} from '../../../../common/searchType';
import {createDataTestId} from '../../../../utils/dataTestId';

interface Props {
    filter: ISearchCondition;
    column: ISearchField;
    session: string;
    componentClasses?: string;
    contentClasses?: string;
    withSort?: boolean;
    isCancelBtnOnHover?: boolean;
    dropdownWidth?: number;
    withTitle?: boolean;
    onRemoveFilter: (columnID: number | Long) => void;
    onUpdateFilter: (filter: ISearchCondition) => void;
    scrollToBottom: () => void;
    onSelectClose?: () => void;
}

const FilterBox = memo(
    ({
        filter: searchFilter,
        column,
        session,
        componentClasses,
        dropdownWidth = 600,
        withTitle = true,
        contentClasses,
        withSort = true,
        isCancelBtnOnHover = true,
        onRemoveFilter,
        onUpdateFilter,
        scrollToBottom,
        onSelectClose
    }: Props) => {
        const {setRef, isHovering} = useHover();
        const [filter, setFilter] = useState(searchFilter);
        const isBoolean = filter.type === ColumnType.Bool;
        const isText = filter.type === ColumnType.Text;

        const handleSetFilter = (filter: ISearchCondition) => {
            setFilter(filter);
            onUpdateFilter(filter);
        };
        // console.log(toJS(filter));

        const handleAddValue = (values: string[]) => {
            if (filter.type === ColumnType.Text) {
                values = values.reduce((acc: string[], val) => {
                    const value = val.trim();
                    value !== '' && acc.push(value);
                    return acc;
                }, []);
            }

            return handleSetFilter({...filter, values});
        };

        const handleDateChange = (data: {date: string; dynamicDate: string}[]) => {
            const {dateEnum, values} = data.reduce(
                (acc, {date, dynamicDate}) => {
                    acc.dateEnum.push(dynamicDate || '');
                    acc.values.push(date || '');
                    return acc;
                },
                {dateEnum: [], values: []}
            );

            return handleSetFilter({
                ...filter,
                dateEnum,
                values
            });
        };

        const handleRemoveValue = (value: string) => {
            const values = [...filter.values];
            const valueToRemoveIndex = values.findIndex(val => val === value);
            values.splice(valueToRemoveIndex, 1);
            handleSetFilter({...filter, values});
        };

        const handleToggleSort = () => {
            handleSetFilter({...filter, isSort: !filter.isSort});
        };

        const handleToggleDesc = () => {
            handleSetFilter({...filter, isdesc: !filter.isdesc});
        };

        const handleChangeOperator = (operator: Operator) => {
            if (
                operator === Operator.Empty ||
                operator === Operator.NotEmpty ||
                filter.type === ColumnType.Date
            ) {
                handleSetFilter({...filter, operator, values: [], dateEnum: []});
            } else {
                handleSetFilter({...filter, operator});
            }
        };

        const handleRemoveFilter = () => {
            onRemoveFilter(filter.columnID);
        };

        const isBetween = filter.operator === Operator.Between;

        const Content = useMemo(
            () => (
                <Flex
                    {...createDataTestId('filter-box-operatorControl-container', true)}
                    componentClasses={cx(styles.controlsWrapper, contentClasses)}
                    alignItems="flex-start"
                    justifyContent="flex-start"
                >
                    {!isBoolean && (
                        <Operators
                            column={column}
                            filter={filter}
                            onChangeOperator={handleChangeOperator}
                        />
                    )}
                    <Control
                        column={column}
                        session={session}
                        dropdownWidth={dropdownWidth}
                        filter={filter}
                        scrollToBottom={scrollToBottom}
                        onUpdateFilter={handleAddValue}
                        onRemoveValue={handleRemoveValue}
                        onSelectClose={onSelectClose}
                        onDateChange={handleDateChange}
                    />
                </Flex>
            ),
            [filter]
        );

        const getTagsValues = useCallback(
            () =>
                filter.values.map(value =>
                    value === APP_SEARCH_CURR_USER
                        ? getLocalizedText(texts.CurrentUserSearch)
                        : value
                ),
            [filter]
        );
        return (
            <Container
                //TO DO : add props to priority-styles
                {...createDataTestId('filter-box-container', true)}
                active={isCancelBtnOnHover && isHovering}
                containerRef={setRef}
                onClose={handleRemoveFilter}
                roundConer
                componentClasses={componentClasses}
            >
                {withTitle && (
                    <Text skin="subtitle-2" textColor="secondary-500">
                        {filter.title}
                    </Text>
                )}
                {Content}
                <Flex
                    componentClasses={styles.filterBoxFooter}
                    justifyContent={isText || isBetween ? 'space-between' : 'flex-end'}
                >
                    {isBetween && (
                        <Text skin="label-1" textColor="primary-700">
                            {getLocalizedText(texts.filtersRangeOneField)}
                        </Text>
                    )}
                    {isText && (
                        <TagsArea
                            tags={getTagsValues()}
                            isActive={isHovering}
                            onRemoveTag={handleRemoveValue}
                        />
                    )}
                    {withSort && (
                        <Sort
                            filter={filter}
                            onToggleDesc={handleToggleDesc}
                            onToggleSort={handleToggleSort}
                        />
                    )}
                </Flex>
            </Container>
        );
    }
);

const Sort = ({onToggleDesc, onToggleSort, filter}) => (
    <Flex inline componentClasses="sort-row">
        <div>
            <Text skin="label-1" textColor="secondary-500" endPadding>
                {getLocalizedText(texts.filtersSort)}
            </Text>
        </div>

        <ToggleSwitch
            id={`${filter.columnID}`}
            checked={!!filter.isSort}
            onChange={() => onToggleSort()}
        />

        <SortIcon
            isDesc={filter.isdesc}
            onClick={onToggleDesc}
            componentClasses={styles.sortIcon}
        />
    </Flex>
);

export default FilterBox;
